import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { UserService } from 'src/app/shared/services/user.service';
import { TableColumn, TableAction } from 'src/app/shared/components/data-table/data-table.component';
import { CreateLookupDialogComponent } from './create-lookup-dialog/create-lookup-dialog.component';
import { CategoryTreeComponent } from './category-tree/category-tree.component';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

export interface LookupTypeConfig {
  value: string;
  label: string;
  columns: TableColumn[];
  mapFn: (item: any) => any;
}

@Component({
  selector: 'app-lookups',
  templateUrl: './lookups.component.html',
  styleUrls: ['./lookups.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LookupsComponent implements OnInit {
  AppConstants = AppConstants;
  lookupData: any[] = [];
  tableColumns: TableColumn[] = [];
  selectedLookupType: string = 'currencies';
  showConfirmDialog = false;
  itemToDelete: any = null;

  @ViewChild(CategoryTreeComponent) categoryTree?: CategoryTreeComponent;

  get isCategoryTree(): boolean { return this.selectedLookupType === 'category-tree'; }

  get isSecretaryUser(): boolean {
    const role = (this.userService.user?.Role || '').toString().trim().toUpperCase().replace(/\s+/g, '');
    return role === 'SECRETARY' || role === 'TENDERCOMMITTEESECRETARY';
  }

  get canShowAddButton(): boolean {
    return !this.isCategoryTree || !this.isSecretaryUser;
  }

  private _loaderService = inject(LoaderService);
  private apiService = inject(ApiService);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private toasterService = inject(ToasterService);
  private userService = inject(UserService);

  lookupTypes: { value: string; label: string }[] = [
    { value: 'currencies', label: 'Currencies' },
    { value: 'countries', label: 'Countries' },
    { value: 'methods-of-payment', label: 'Methods of Payment' },
    { value: 'category-tree', label: 'Category tree' },
    { value: 'terms-of-payment', label: 'Terms of Payment' },
    { value: 'departments', label: 'Departments' },
    { value: 'banks', label: 'Banks' },
    { value: 'documents-master', label: 'Documents Master' }
  ];

  private readonly actionIcons: TableAction[] = [
    { iconPath: 'assets/icons/edit-icon.svg', action: 'edit', title: 'Edit' },
    { iconPath: 'assets/icons/delete-icon.svg', action: 'delete', title: 'Delete' }
  ];

  private readonly columnConfigs: Record<string, TableColumn[]> = {
    'currencies': [
      { field: 'id', header: 'ID', type: 'text', sortable: true },
      { field: 'currencyId', header: 'Currency ID', type: 'text', sortable: true },
      { field: 'description', header: 'Description', type: 'text', sortable: true },
      { field: 'isDefault', header: 'Default', type: 'badge', sortable: true },
      { field: 'actions', header: 'Actions', type: 'action', actions: this.actionIcons }
    ],
    'countries': [
      { field: 'id', header: 'ID', type: 'text', sortable: true },
      { field: 'countryRegionId', header: 'Country ID', type: 'text', sortable: true },
      { field: 'countryName', header: 'Country Name', type: 'text', sortable: true },
      { field: 'actions', header: 'Actions', type: 'action', actions: this.actionIcons }
    ],
    'methods-of-payment': [
      { field: 'id', header: 'ID', type: 'text', sortable: true },
      { field: 'code', header: 'Code', type: 'text', sortable: true },
      { field: 'description', header: 'Description', type: 'text', sortable: true },
      { field: 'isDefault', header: 'Default', type: 'badge', sortable: true },
      { field: 'actions', header: 'Actions', type: 'action', actions: this.actionIcons }
    ],
    'category-tree': [
      { field: 'id', header: 'ID', type: 'text', sortable: true },
      { field: 'code', header: 'Code', type: 'text', sortable: true },
      { field: 'description', header: 'Description', type: 'text', sortable: true },
      { field: 'actions', header: 'Actions', type: 'action', actions: this.actionIcons }
    ],
    'terms-of-payment': [
      { field: 'id', header: 'ID', type: 'text', sortable: true },
      { field: 'code', header: 'Code', type: 'text', sortable: true },
      { field: 'description', header: 'Description', type: 'text', sortable: true },
      { field: 'isDefault', header: 'Default', type: 'badge', sortable: true },
      { field: 'actions', header: 'Actions', type: 'action', actions: this.actionIcons }
    ],
    'departments': [
      { field: 'id', header: 'ID', type: 'text', sortable: true },
      { field: 'departmentId', header: 'Department ID', type: 'text', sortable: true },
      { field: 'departmentName', header: 'Department Name', type: 'text', sortable: true },
      { field: 'isDefault', header: 'Default', type: 'badge', sortable: true },
      { field: 'actions', header: 'Actions', type: 'action', actions: this.actionIcons }
    ],
    'banks': [
      { field: 'id', header: 'ID', type: 'text', sortable: true },
      { field: 'bankId', header: 'Bank ID', type: 'text', sortable: true },
      { field: 'description', header: 'Description', type: 'text', sortable: true },
      { field: 'isDefault', header: 'Default', type: 'badge', sortable: true },
      { field: 'actions', header: 'Actions', type: 'action', actions: this.actionIcons }
    ],
    'documents-master': [
      { field: 'id', header: 'ID', type: 'text', sortable: true },
      { field: 'documentName', header: 'Document Name', type: 'text', sortable: true },
      { field: 'section', header: 'Section', type: 'text', sortable: true },
      { field: 'processName', header: 'Process Name', type: 'text', sortable: true },
      { field: 'documentFor', header: 'Document For', type: 'text', sortable: true },
      { field: 'documentType', header: 'Document Type', type: 'text', sortable: true },
      { field: 'mandatory', header: 'Mandatory', type: 'badge', sortable: true },
      { field: 'fieldHint', header: 'Field Hint', type: 'text', sortable: true },
      { field: 'expirationDate', header: 'Expiration Date Visible', type: 'badge', sortable: true },
      { field: 'expirationDateMandatory', header: 'Expiration Date Required', type: 'badge', sortable: true },
      { field: 'allowMultipleDocs', header: 'Multiple Documents', type: 'badge', sortable: true },
      { field: 'checkBoxRequired', header: 'Checkbox Required', type: 'badge', sortable: true },
      { field: 'additionalDetailsRequired', header: 'Additional Details Required', type: 'badge', sortable: true },
      { field: 'position', header: 'Position', type: 'text', sortable: true },
      { field: 'actions', header: 'Actions', type: 'action', actions: this.actionIcons }
    ]
  };

  ngOnInit(): void {
    this.onLookupTypeChange();
  }

  onLookupTypeChange(): void {
    this.tableColumns = this.columnConfigs[this.selectedLookupType] || [];
    this.fetchLookupData();
  }

  async fetchLookupData(): Promise<void> {
    if (this.selectedLookupType === 'category-tree') {
      this.lookupData = [];
      this.cdr.markForCheck();
      return;
    }

    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.Lookups}/${this.selectedLookupType}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));

      const rawData = Array.isArray(response) ? response : (response?.data || response?.Data || []);
      this.lookupData = Array.isArray(rawData) ? rawData.map((item: any) => this.mapItem(item)) : [];
      this.cdr.markForCheck();
    } catch (error) {
      console.error(`[Lookups] Error fetching ${this.selectedLookupType}:`, error);
      this.lookupData = [];
      this.cdr.markForCheck();
    } finally {
      this._loaderService.hideLoader();
    }
  }

  private mapItem(item: any): any {
    const base = {
      id: item.id || item.ID || '',
      isDefault: item.isDefaultValue !== undefined ? String(item.isDefaultValue) : (item.IsDefaultValue !== undefined ? String(item.IsDefaultValue) : '')
    };

    switch (this.selectedLookupType) {
      case 'currencies':
        return { ...base, currencyId: item.currencyId || item.CurrencyId || '', description: item.description || item.Description || '' };
      case 'countries':
        return { ...base, countryRegionId: item.countryRegionId || item.CountryRegionId || '', countryName: item.countryName || item.CountryName || '' };
      case 'methods-of-payment':
        return {
          ...base,
          code: item.methodOfPaymentId || item.MethodOfPaymentId || '',
          methodOfPaymentId: item.methodOfPaymentId || item.MethodOfPaymentId || '',
          description: item.description || item.Description || ''
        };
      case 'terms-of-payment':
        return {
          ...base,
          code: item.termsOfPaymentId || item.TermsOfPaymentId || '',
          termsOfPaymentId: item.termsOfPaymentId || item.TermsOfPaymentId || '',
          description: item.description || item.Description || ''
        };
      case 'departments':
        return { ...base, departmentId: item.departmentId || item.DepartmentId || '', departmentName: item.departmentName || item.DepartmentName || '' };
      case 'banks':
        return { ...base, bankId: item.bankId || item.BankId || '', description: item.description || item.Description || '' };
      case 'documents-master':
        return {
          ...base,
          documentName: item.documentName || item.DocumentName || '',
          section: item.section || item.Section || '',
          processName: item.processName || item.ProcessName || '',
          parmDocumentFor: item.parmDocumentFor || item.ParmDocumentFor || item.documentFor || '',
          documentFor: item.parmDocumentFor || item.ParmDocumentFor || item.documentFor || '',
          documentType: item.documentType || item.DocumentType || '',
          mandatory: item.mandatory !== undefined ? String(item.mandatory) : (item.Mandatory !== undefined ? String(item.Mandatory) : ''),
          fieldHint: item.fieldHint || item.FieldHint || '',
          expirationDate: item.expirationDate !== undefined ? String(item.expirationDate) : (item.ExpirationDate !== undefined ? String(item.ExpirationDate) : ''),
          expirationDateMandatory: item.expirationDateMandatory !== undefined ? String(item.expirationDateMandatory) : (item.ExpirationDateMandatory !== undefined ? String(item.ExpirationDateMandatory) : ''),
          allowMultipleDocs: item.allowMultipleDocs !== undefined ? String(item.allowMultipleDocs) : (item.AllowMultipleDocs !== undefined ? String(item.AllowMultipleDocs) : ''),
          checkBoxRequired: item.checkBoxRequired !== undefined ? String(item.checkBoxRequired) : (item.CheckBoxRequired !== undefined ? String(item.CheckBoxRequired) : ''),
          additionalDetailsRequired: item.additionalDetailsRequired !== undefined ? String(item.additionalDetailsRequired) : (item.AdditionalDetailsRequired !== undefined ? String(item.AdditionalDetailsRequired) : ''),
          position: item.position !== undefined ? item.position : (item.Position !== undefined ? item.Position : 0)
        };
      default:
        return { ...base, code: item.code || item.Code || '', description: item.description || item.Description || '' };
    }
  }

  onTableActionClick(event: { action: string, row: any }): void {
    if (event.action === 'edit') {
      this.openDialog(event.row);
    } else if (event.action === 'delete') {
      this.itemToDelete = event.row;
      this.showConfirmDialog = true;
      this.cdr.markForCheck();
    }
  }

  openAddDialog(): void {
    if (!this.canShowAddButton) return;

    if (this.isCategoryTree) {
      this.categoryTree?.openAddCategory();
    } else {
      this.openDialog(null);
    }
  }

  private openDialog(row: any | null): void {
    const dialogRef = this.dialog.open(CreateLookupDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      panelClass: 'create-lookup-dialog',
      data: { lookupType: this.selectedLookupType, item: row }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.fetchLookupData();
      }
    });
  }

  cancelDelete(): void {
    this.showConfirmDialog = false;
    this.itemToDelete = null;
    this.cdr.markForCheck();
  }

  async confirmDelete(): Promise<void> {
    if (!this.itemToDelete) {
      this.showConfirmDialog = false;
      return;
    }

    this.showConfirmDialog = false;
    this._loaderService.showLoader();

    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.Lookups}/${this.selectedLookupType}/${this.itemToDelete.id}`;
      const response: any = await lastValueFrom(this.apiService.deleteByUrl(url, {}, {}));

      if (response && (response.isSuccess || response.IsSuccess || response.data !== undefined)) {
        this.toasterService.showSuccess('Record deleted successfully', 'Success');
        this.fetchLookupData();
      } else {
        const errorMsg = response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to delete record';
        this.toasterService.showError(errorMsg, 'Error');
      }
    } catch (error: any) {
      const errorMsg = error?.error?.errorMessageEn || error?.message || 'An error occurred while deleting';
      this.toasterService.showError(errorMsg, 'Error');
      console.error('[Lookups] Delete error:', error);
    } finally {
      this._loaderService.hideLoader();
      this.itemToDelete = null;
      this.cdr.markForCheck();
    }
  }

  getSelectedLabel(): string {
    return this.lookupTypes.find(t => t.value === this.selectedLookupType)?.label || '';
  }
}
